import { useRef, useState } from "react";
import { UploadCloud, X, FileText } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export const inputCls = "w-full border border-[#102A43]/20 bg-white px-4 py-3 text-[15px] text-[#102A43] placeholder:text-[#7a8ea2] outline-none transition-colors focus:border-[#1769AA] focus:ring-2 focus:ring-[#1769AA]/20 rounded-[2px]";

export const Field = ({ label, id, required, error, children, hint }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#102A43]">
      {label}{required && <span className="text-[#1769AA]"> *</span>}
    </Label>
    {children}
    {hint && !error && <p className="text-xs text-[#526579]">{hint}</p>}
    {error && <p className="text-xs text-[#b42318]" role="alert" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

export const Text = ({ id, value, onChange, textarea, ...rest }) => {
  const C = textarea ? "textarea" : "input";
  return <C id={id} name={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} ${textarea ? "min-h-[130px]" : ""}`} data-testid={`${id}-input`} {...rest} />;
};

export const Choice = ({ id, value, onChange, options, placeholder = "Select…" }) => (
  <select id={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%228%22><path d=%22M1 1l5 5 5-5%22 fill=%22none%22 stroke=%22%23102A43%22/></svg>')] bg-no-repeat bg-[right_1rem_center]`} data-testid={`${id}-select`}>
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export const Honeypot = ({ value, onChange }) => (
  <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
    <label htmlFor="website">Website</label>
    <input id="website" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

export const Consent = ({ id = "consent", checked, onChange, error, children }) => (
  <div>
    <div className="flex items-start gap-3">
      <Checkbox id={id} checked={!!checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5 rounded-none border-[#102A43]/40 data-[state=checked]:bg-[#1769AA] data-[state=checked]:border-[#1769AA]" data-testid={`${id}-checkbox`} />
      <label htmlFor={id} className="text-sm leading-relaxed text-[#526579]">{children}</label>
    </div>
    {error && <p className="mt-2 text-xs text-[#b42318]" role="alert">{error}</p>}
  </div>
);

const fmt = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const FileDrop = ({ id, files, setFiles, accept, max = 10, maxMb = 25, multiple = true, label = "Drop files here or browse" }) => {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState("");
  const exts = accept.split(",").map((e) => e.trim().replace(".", "").toLowerCase());
  const add = (list) => {
    const next = [...files]; let msg = "";
    Array.from(list).forEach((f) => {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!exts.includes(ext)) msg = `${f.name}: file type not accepted.`;
      else if (f.size > maxMb * 1048576) msg = `${f.name}: exceeds ${maxMb} MB.`;
      else if (next.length >= max) msg = `Maximum ${max} file${max > 1 ? "s" : ""}.`;
      else if (!multiple) next.splice(0, next.length, f);
      else next.push(f);
    });
    setErr(msg); setFiles(next);
  };
  return (
    <div>
      <div role="button" tabIndex={0} data-testid={`${id}-dropzone`}
        onClick={() => ref.current?.click()} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && ref.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        className={`flex cursor-pointer flex-col items-center justify-center gap-3 border border-dashed px-6 py-10 text-center transition-colors ${drag ? "border-[#1769AA] bg-[#1769AA]/5" : "border-[#102A43]/30 bg-[#F3F6F8] hover:border-[#1769AA]"}`}>
        <UploadCloud className="h-7 w-7 text-[#1769AA]" />
        <p className="font-semibold text-[#102A43]">{label}</p>
        <p className="font-mono text-[11px] tracking-wide text-[#526579]">{exts.map((e) => e.toUpperCase()).join(" · ")} — max {maxMb} MB each{multiple ? `, up to ${max} files` : ""}</p>
        <input ref={ref} id={id} type="file" multiple={multiple} accept={accept} className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid={`${id}-input`} />
      </div>
      {err && <p className="mt-2 text-xs text-[#b42318]" role="alert">{err}</p>}
      {files.length > 0 && (
        <ul className="mt-3 divide-y divide-[#102A43]/10 border border-[#102A43]/10" data-testid={`${id}-list`}>
          {files.map((f, i) => (
            <li key={`${f.name}${i}`} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
              <span className="flex min-w-0 items-center gap-2"><FileText className="h-4 w-4 shrink-0 text-[#1769AA]" /><span className="truncate">{f.name}</span><span className="font-mono text-xs text-[#526579]">{fmt(f.size)}</span></span>
              <button type="button" onClick={() => setFiles(files.filter((_, k) => k !== i))} aria-label={`Remove ${f.name}`} className="text-[#526579] hover:text-[#b42318]"><X className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const SURVEY_ACCEPT = ".dwg,.dxf,.pdf,.csv,.txt,.xlsx,.xls,.xml,.landxml,.jpg,.jpeg,.png,.tif,.tiff,.zip,.dgn,.las,.laz,.e57,.kml,.kmz,.rw5,.fbk,.jxl,.dat";
export const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v || "");
